import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import InvoiceStatusBadge from '@/components/invoices/InvoiceStatusBadge';
import { formatCurrency, formatDate } from '@/lib/format';
import { Invoice } from '@/types/models';
import { getInvoicesByFamily, getInvoiceLineItems, createInvoicePaymentSession, updateInvoice } from '@/services/invoiceService';
import { MailPlus, Plus, ReceiptText, CreditCard, ExternalLink, Copy, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { eventEmitter, EVENTS } from '@/utils/eventEmitter';

interface AdminFamilyInvoicesCardProps {
  familyId: string;
  onSendInvoice?: (invoice: Invoice) => void;
  onCreateInvoice?: () => void;
  onInvoiceListReady?: (refreshFn: () => void) => void;
}

const AdminFamilyInvoicesCard: React.FC<AdminFamilyInvoicesCardProps> = ({
  familyId,
  onSendInvoice,
  onCreateInvoice,
  onInvoiceListReady
}) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [lineItems, setLineItems] = useState<{ id: string; description: string; amount_cents: number; }[]>([]);
  const [lineItemsLoading, setLineItemsLoading] = useState(false);
  const [paymentLinkLoading, setPaymentLinkLoading] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState<string | null>(null);
  const [markingPaid, setMarkingPaid] = useState<string | null>(null);
  const [confirmPaidDialogOpen, setConfirmPaidDialogOpen] = useState(false);
  const [invoiceToMarkPaid, setInvoiceToMarkPaid] = useState<Invoice | null>(null);

  const fetchInvoices = async () => {
    if (!familyId) {
      setLoading(false);
      setError('No family ID provided');
      return;
    }
    
    try {
      setLoading(true);
      const data = await getInvoicesByFamily(familyId);
      setInvoices(data);
      setError(null);
    } catch (e) {
      setError('Failed to load invoices');
      console.error('Error loading family invoices:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [familyId]);

  // Expose the refresh function to parent component
  useEffect(() => {
    if (onInvoiceListReady) {
      onInvoiceListReady(fetchInvoices);
    }
  }, [onInvoiceListReady]);

  // Listen for payment/invoice update events to refresh data
  useEffect(() => {
    const handleRefreshInvoices = () => {
      fetchInvoices();
    };

    eventEmitter.on(EVENTS.REFRESH_INVOICES, handleRefreshInvoices);
    eventEmitter.on(EVENTS.PAYMENT_RECORDED, handleRefreshInvoices);

    return () => {
      eventEmitter.off(EVENTS.REFRESH_INVOICES, handleRefreshInvoices);
      eventEmitter.off(EVENTS.PAYMENT_RECORDED, handleRefreshInvoices);
    };
  }, []);

  const handleOpenModal = async (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setModalOpen(true);
    setLineItemsLoading(true);
    try {
      const items = await getInvoiceLineItems(invoice.id);
      setLineItems(items);
    } catch (error) {
      console.error('Error loading line items:', error);
      toast.error('Failed to load invoice details');
    } finally {
      setLineItemsLoading(false);
    }
  };

  const handleGeneratePaymentLink = async () => {
    if (!selectedInvoice) return;
    
    setPaymentLinkLoading(true);
    try {
      const session = await createInvoicePaymentSession({
        invoiceId: selectedInvoice.id,
        familyId: selectedInvoice.family_id,
        organizationId: selectedInvoice.organization_id,
        amount_cents: selectedInvoice.total_cents,
        customer_email: '', // Admin doesn't need to specify email
        success_url: window.location.origin + '/payment-success',
        cancel_url: window.location.origin + '/payment-cancelled',
      });
      
      if (session?.url) {
        // Copy to clipboard and show success
        await navigator.clipboard.writeText(session.url);
        toast.success('Payment link copied to clipboard!');
      } else {
        throw new Error('Failed to generate payment link');
      }
    } catch (error) {
      console.error('Error generating payment link:', error);
      toast.error('Failed to generate payment link');
    } finally {
      setPaymentLinkLoading(false);
    }
  };

  const handleSendInvoice = async (invoice: Invoice) => {
    setSendingInvoice(invoice.id);
    try {
      if (onSendInvoice) {
        await onSendInvoice(invoice);
      } else {
        toast.info('Send invoice functionality will be available soon');
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error('Failed to send invoice');
    } finally {
      setSendingInvoice(null);
    }
  };

  const copyInvoiceId = async (invoiceId: string) => {
    try {
      await navigator.clipboard.writeText(invoiceId);
      toast.success('Invoice ID copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy invoice ID');
    }
  };

  const handleMarkAsPaid = async (invoice: Invoice) => {
    setMarkingPaid(invoice.id);
    try {
      const updatedInvoice = await updateInvoice(invoice.id, { status: 'paid' });
      if (updatedInvoice) {
        toast.success('Invoice marked as paid');
        // Update the local state
        setInvoices(prevInvoices => 
          prevInvoices.map(inv => 
            inv.id === invoice.id ? { ...inv, status: 'paid' } : inv
          )
        );
        // Update selected invoice if it's the same one
        if (selectedInvoice?.id === invoice.id) {
          setSelectedInvoice({ ...selectedInvoice, status: 'paid' });
        }
      } else {
        throw new Error('Failed to update invoice');
      }
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      toast.error('Failed to mark invoice as paid');
    } finally {
      setMarkingPaid(null);
    }
  };

  return (
    <>
      <Card className="w-full">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div className="space-y-1">
            <CardTitle className="text-xl flex items-center gap-2">
              <ReceiptText className="h-5 w-5" />
              Family Invoices ({invoices.length})
            </CardTitle>
            <CardDescription>Manage invoices and billing for this family</CardDescription>
          </div>
          {onCreateInvoice && (
            <Button onClick={onCreateInvoice} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Create Invoice
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="ml-2 text-sm text-muted-foreground">Loading invoices...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500 mb-4">{error}</p>
              <Button onClick={() => window.location.reload()} variant="outline">
                Retry
              </Button>
            </div>
          ) : invoices.length > 0 ? (
            <div className="space-y-4">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-semibold text-lg">
                          {formatCurrency(invoice.total_cents / 100)}
                        </span>
                        <InvoiceStatusBadge status={invoice.status} />
                      </div>
                      
                      {/* Enhanced metadata display */}
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-muted-foreground">
                        <div>
                          <span className="font-medium">Due:</span> {invoice.due_date ? formatDate(invoice.due_date) : 'No due date'}
                        </div>
                        <div>
                          <span className="font-medium">Created:</span> {formatDate(invoice.created_at)}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="font-medium">Invoice Number:</span>
                          <code className="text-xs bg-muted px-1 rounded">
                            {invoice.invoice_number || 'PENDING'}
                          </code>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-4 w-4 p-0"
                            onClick={() => copyInvoiceId(invoice.invoice_number || invoice.id)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      {invoice.status !== 'paid' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSendInvoice(invoice)}
                            disabled={sendingInvoice === invoice.id}
                            className="flex items-center gap-1"
                          >
                            {sendingInvoice === invoice.id ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b border-current"></div>
                                Sending...
                              </>
                            ) : (
                              <>
                                <MailPlus className="h-3 w-3" />
                                Send
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleMarkAsPaid(invoice)}
                            disabled={markingPaid === invoice.id}
                            className="flex items-center gap-1 bg-green-600 hover:bg-green-700"
                          >
                            {markingPaid === invoice.id ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                                Marking...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="h-3 w-3" />
                                Mark Paid
                              </>
                            )}
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleOpenModal(invoice)}
                        className="flex items-center gap-1"
                      >
                        <ReceiptText className="h-3 w-3" />
                        Details
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <ReceiptText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="mb-4">No invoices found for this family</p>
              {onCreateInvoice && (
                <Button onClick={onCreateInvoice} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Invoice
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Invoice Details Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md w-full p-0 rounded-lg overflow-hidden">
          <DialogHeader className="p-4 border-b">
            <DialogTitle>Invoice Details</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-lg">{formatCurrency(selectedInvoice.total_cents / 100)}</div>
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    Invoice ID: 
                    <code className="bg-muted px-1 rounded">{selectedInvoice.id.slice(-8)}</code>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-4 w-4 p-0"
                      onClick={() => copyInvoiceId(selectedInvoice.id)}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <InvoiceStatusBadge status={selectedInvoice.status} />
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium">Due Date:</span>
                  <span>{selectedInvoice.due_date ? formatDate(selectedInvoice.due_date) : 'No due date'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Created:</span>
                  <span>{formatDate(selectedInvoice.created_at)}</span>
                </div>
                {selectedInvoice.issue_date && (
                  <div className="flex justify-between">
                    <span className="font-medium">Issued:</span>
                    <span>{formatDate(selectedInvoice.issue_date)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-medium">Invoice Number:</span>
                  <span className="font-mono">{selectedInvoice.invoice_number || 'PENDING'}</span>
                </div>
              </div>
              
              <div>
                <div className="font-medium mb-2">Line Items</div>
                {lineItemsLoading ? (
                  <div className="py-4 text-center text-muted-foreground">Loading...</div>
                ) : lineItems.length === 0 ? (
                  <div className="py-4 text-center text-muted-foreground">No line items found.</div>
                ) : (
                  <div className="max-h-40 overflow-y-auto divide-y border rounded">
                    {lineItems.map((item) => (
                      <div key={item.id} className="flex justify-between py-2 px-3 text-sm">
                        <span>{item.description}</span>
                        <span className="font-medium">{formatCurrency(item.amount_cents / 100)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {selectedInvoice.status !== 'paid' && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button
                      className="flex-1"
                      onClick={handleGeneratePaymentLink}
                      disabled={paymentLinkLoading}
                      variant="outline"
                    >
                      {paymentLinkLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      ) : (
                        <Copy className="h-4 w-4 mr-2" />
                      )}
                      Copy Payment Link
                    </Button>
                    <Button
                      onClick={() => handleSendInvoice(selectedInvoice)}
                      disabled={sendingInvoice === selectedInvoice.id}
                      className="flex-1"
                    >
                      {sendingInvoice === selectedInvoice.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : (
                        <MailPlus className="h-4 w-4 mr-2" />
                      )}
                      Send Invoice
                    </Button>
                  </div>
                  <Button
                    onClick={() => handleMarkAsPaid(selectedInvoice)}
                    disabled={markingPaid === selectedInvoice.id}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {markingPaid === selectedInvoice.id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Marking as Paid...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Mark as Paid
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminFamilyInvoicesCard;